/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ReleaseNoteProps {
  id: string;
  releaseNumber: string;
  description: string;
  releaseDate: Date | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ReleaseNoteRecord extends Record<ReleaseNoteProps>({
  id: null as any,
  releaseNumber: null as any,
  description: null as any,
  releaseDate: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    releaseNumber: string;
    description: string;
    releaseDate: string | number | Date | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ReleaseNoteRecord {
    if (json == null) {
      throw new Error('ReleaseNoteRecord.fromJS: parameter cannot be null');
    }
    return new ReleaseNoteRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ReleaseNoteJson.id',
      ),
      releaseNumber: getFromOrThrow(json, (j) => j.releaseNumber, 'ReleaseNoteJson.releaseNumber'),
      description: getFromOrThrow(json, (j) => j.description, 'ReleaseNoteJson.description'),
      releaseDate: json.releaseDate != null ? new Date(json.releaseDate) : null,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    releaseNumber: string;
    description: string;
    releaseDate: string | number | Date | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ReleaseNoteRecord, Error[]] {
    if (json == null) {
      throw new Error('ReleaseNoteRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ReleaseNoteRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ReleaseNoteJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      releaseNumber: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.releaseNumber, 'ReleaseNoteJson.releaseNumber'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      description: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.description, 'ReleaseNoteJson.description'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      releaseDate: tryOrDefault(
        () => (json.releaseDate != null ? new Date(json.releaseDate) : null),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ReleaseNoteProps) {
    super(props);
    if (props == null) {
      throw new Error('ReleaseNoteRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ReleaseNoteViewModelBase implements ReleaseNoteProps {
  protected readonly _props: ReleaseNoteProps;
  private _json?: ReleaseNoteProps;

  get id(): string {
    return this._props.id;
  }
  get releaseNumber(): string {
    return this._props.releaseNumber;
  }
  get description(): string {
    return this._props.description;
  }
  get releaseDate(): Date | null {
    return this._props.releaseDate;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: ReleaseNoteProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        releaseNumber: this._props.releaseNumber,
        description: this._props.description,
        releaseDate: this._props.releaseDate,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllReleaseNoteEntities = (state: AppState) =>
  state.entityStores.releaseNote.entities;

export const selectReleaseNoteById = (id: string) => (state: AppState) =>
  state.entityStores.releaseNote.entities.get(id);

export const selectReleaseNoteMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.releaseNote.meta.get(id);

export const selectReleaseNoteAndMetadataById = (id: string) =>
  createSelector(
    selectReleaseNoteById(id),
    selectReleaseNoteMetadataById(id),
    (releaseNote, meta) => ({ releaseNote, meta }),
  );

export function selectReleaseNoteEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllReleaseNoteEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ReleaseNoteRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectReleaseNoteEntitiesByReleaseNumber(releaseNumber: string) {
  return createSelector(selectAllReleaseNoteEntities, (entities) =>
    entities.filter((e) => e.releaseNumber === releaseNumber).valueSeq(),
  );
}

export function selectReleaseNoteEntitiesByDescription(description: string) {
  return createSelector(selectAllReleaseNoteEntities, (entities) =>
    entities.filter((e) => e.description === description).valueSeq(),
  );
}

export function selectReleaseNoteEntitiesByReleaseDate(releaseDate: Date) {
  return createSelector(selectAllReleaseNoteEntities, (entities) =>
    entities.filter((e) => e.releaseDate === releaseDate).valueSeq(),
  );
}
//#endregion Selectors
